import { Refine } from "@refinedev/core";
import { BrowserRouter, Outlet, Route, Routes, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { Layout } from './components/layout/Layout';
import { LoadingScreen } from './components/common/LoadingScreen';
import { LoginForm } from './components/auth/LoginForm';
import { supabaseClient } from "./lib/supabase";
import { dataProvider, liveProvider } from "@refinedev/supabase";
import { UserList } from "./components/users/UserList";
import { UserCreate } from "./components/users/UserCreate";
import { UserEdit } from "./components/users/UserEdit";
import { UserShow } from "./components/users/UserShow";
import { ProjectList } from "./components/projects/ProjectList";
import { ProjectCreate } from "./components/projects/ProjectCreate";
import { ProjectEdit } from "./components/projects/ProjectEdit";
import { ProjectShow } from "./components/projects/ProjectShow";
import { AnalysisCreate } from "./components/analyses/AnalysisCreate";
import { AnalysisShow } from "./components/analyses/AnalysisShow";
import { TextEditor } from "./components/analyses/editor/TextEditor";
import { APIServicesPage } from "./components/api-services";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { useAuth } from './contexts/AuthContext';
import AIPage from './components/analyses/AIPage';
import PromptsPage from './components/prompts/PromptsPage';
import { WikiPage } from './components/wiki/WikiPage';
import { StopWordsPage } from './components/stop-words/StopWordsPage';
import { ProjectStats } from './components/dashboard/ProjectStats';
import { BatchFunctionsPage } from './components/batch/BatchFunctionsPage';
import { FilteredKeywordsProvider } from './contexts/FilteredKeywordsContext';

const Instructions = () => (
  <div className="bg-white rounded-lg shadow p-6">
    <h1 className="text-2xl font-bold mb-4">Instructions</h1>
    <p>Usage instructions and documentation will be shown here.</p>
  </div>
);

const ProtectedRoute = ({ children, roles }: { children: React.ReactNode; roles?: string[] }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check role access if roles are specified
  if (roles) {
    const userRole = 
      user.user_metadata?.role || 
      user.role ||
      (user.email === 'andmaillev@gmail.com' ? 'admin' : 'client');

    // Allow access if user is admin or has required role
    const hasAccess = userRole === 'admin' || 
                     user.email === 'andmaillev@gmail.com' ||
                     roles.includes(userRole);

    if (!hasAccess) {
      return <Navigate to="/" replace />;
    }
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Refine
            dataProvider={dataProvider(supabaseClient)}
            liveProvider={liveProvider(supabaseClient)}
            authProvider={{
              login: async ({ email, password }) => {
                const { error } = await supabaseClient.auth.signInWithPassword({
                  email,
                  password,
                });
                if (error) {
                  return {
                    success: false,
                    error,
                  };
                }
                return {
                  success: true,
                  redirectTo: "/",
                };
              },
              logout: async () => {
                const { error } = await supabaseClient.auth.signOut();
                if (error) {
                  return {
                    success: false,
                    error,
                  };
                }
                return {
                  success: true,
                  redirectTo: "/login",
                };
              },
              check: async () => {
                try {
                  const { data } = await supabaseClient.auth.getSession();
                  const { session } = data;
                  
                  if (!session) {
                    return {
                      authenticated: false,
                      redirectTo: "/login",
                      error: new Error("No session found"),
                    };
                  }
                  
                  return {
                    authenticated: true,
                  };
                } catch (error) {
                  return {
                    authenticated: false,
                    redirectTo: "/login",
                    error: new Error("Failed to check auth state"),
                  };
                }
              },
              onError: async (error) => {
                console.error("Auth error:", error);
                return { error };
              },
              getPermissions: async () => {
                const { data } = await supabaseClient.auth.getSession();
                return data.session?.user.user_metadata.role;
              },
              getIdentity: async () => {
                const { data } = await supabaseClient.auth.getUser();
                if (data?.user) {
                  return {
                    ...data.user,
                    name: data.user.email,
                  };
                }
                return null;
              },
            }}
            resources={[
              {
                name: "dashboard",
                list: "/",
                meta: {
                  label: "Dashboard",
                  icon: "Dashboard",
                },
              },
              {
                name: "projects",
                list: "/projects",
                show: "/projects/show/:id",
                create: "/projects/create",
                edit: "/projects/edit/:id",
                meta: {
                  label: "Projects",
                  icon: "Projects",
                },
              },
              {
                name: "users",
                list: "/users",
                show: "/users/show/:id",
                create: "/users/create",
                edit: "/users/edit/:id",
                meta: {
                  label: "Users",
                  icon: "Users",
                },
              },
              {
                name: "api-services",
                list: "/api-services",
                meta: {
                  label: "API Services",
                  icon: "ApiServices",
                },
              },
              {
                name: "prompts",
                list: "/prompts",
                meta: {
                  label: "Prompts",
                  icon: "Prompts",
                },
              },
              {
                name: "stop-words",
                list: "/stop-words",
                meta: {
                  label: "Stop Words",
                  icon: "StopWords",
                },
              },
              {
                name: "batch-functions",
                list: "/batch-functions",
                meta: {
                  label: "Batch Functions",
                  icon: "BatchFunctions",
                },
              },
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
            }}
          >
            <Routes>
              <Route path="/login" element={<LoginForm />} />
              <Route
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Outlet />
                    </Layout>
                  </ProtectedRoute>
                }
              >
                <Route index element={<ProjectStats />} />
                <Route path="/projects" element={<ProjectList />} />
                <Route path="/projects/create" element={<ProjectCreate />} />
                <Route path="/projects/edit/:id" element={<ProjectEdit />} />
                <Route path="/projects/show/:id" element={<ProjectShow />} />
                <Route path="/projects/:projectId/analyses/create" element={<AnalysisCreate />} />
                <Route path="/analyses/:id" element={<AnalysisShow />} />
                <Route path="/analyses/:id/editor" element={
                  <FilteredKeywordsProvider>
                    <TextEditor />
                  </FilteredKeywordsProvider>
                } />
                <Route path="/analyses/:id/ai" element={
                  <FilteredKeywordsProvider>
                    <AIPage />
                  </FilteredKeywordsProvider>
                } />
                <Route path="/wiki" element={<WikiPage />} />
                <Route path="/wiki/:id" element={<WikiPage />} />
                <Route path="/wiki/new" element={
                  <ProtectedRoute roles={['admin']}>
                    <WikiPage />
                  </ProtectedRoute>
                } />
                <Route 
                  path="/stop-words" 
                  element={
                    <ProtectedRoute roles={['admin', 'optimizer']}>
                      <StopWordsPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/prompts" 
                  element={
                    <ProtectedRoute roles={['optimizer', 'admin']}>
                      <PromptsPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/batch-functions" 
                  element={
                    <ProtectedRoute roles={['optimizer', 'admin']}>
                      <BatchFunctionsPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/users" 
                  element={
                    <ProtectedRoute roles={['optimizer', 'admin']}>
                      <UserList />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/users/create" 
                  element={
                    <ProtectedRoute roles={['optimizer', 'admin']}>
                      <UserCreate />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/users/edit/:id" 
                  element={
                    <ProtectedRoute roles={['admin']}>
                      <UserEdit />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/users/show/:id" 
                  element={
                    <ProtectedRoute roles={['admin']}>
                      <UserShow />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/api-services" 
                  element={
                    <ProtectedRoute roles={['admin']}>
                      <APIServicesPage />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/instructions" element={<Instructions />} />
              </Route>
              <Route path="*" element={
                <ProtectedRoute>
                  <Navigate to="/" replace />
                </ProtectedRoute>
              } />
            </Routes>
          </Refine>
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;